/**
 * What the deposit sheet starts with, so the player lands on a ready-to-pay
 * state instead of an empty box (PM, 2026-09-15): R$ 50 pre-selected and the
 * best coupon the quote allows already applied.
 */
export const PREFERRED_DEPOSIT_AMOUNT = 50;

/**
 * The amount each wallet currency opens on (PM: R$ 50 on 2026-09-15, then
 * AR$ 10.000 the same day). 0 = no preference, so the smallest chip wins.
 */
const PREFERRED_BY_CURRENCY: Record<string, number> = { BRL: PREFERRED_DEPOSIT_AMOUNT, ARS: 10_000 };

export function preferredDepositAmount(fiatCurrency: string): number {
  return PREFERRED_BY_CURRENCY[String(fiatCurrency || '').toUpperCase()] ?? 0;
}

/**
 * The amount the sheet opens on. The preferred amount when the channel allows
 * it; otherwise the smallest quick amount at or above it; otherwise the
 * smallest quick amount at all; 0 (empty box) when nothing fits.
 */
export function defaultDepositAmount(input: {
  minAmount: number;
  maxAmount: number;
  quickAmounts: number[];
  allowCustomAmount?: boolean;
  preferred?: number;
}): number {
  const { minAmount, maxAmount, quickAmounts, allowCustomAmount = true, preferred = PREFERRED_DEPOSIT_AMOUNT } = input;
  const inRange = (value: number) => Number.isFinite(value) && value >= minAmount && value <= maxAmount;
  const chips = [...new Set(quickAmounts.map(Number))].filter(inRange).sort((a, b) => a - b);
  if (inRange(preferred) && (allowCustomAmount || chips.includes(preferred))) return preferred;
  return chips.find((value) => value >= preferred) ?? chips[0] ?? 0;
}

export type DefaultableCoupon = {
  id: number;
  code: string;
  status?: string;
  estimatedBonus?: number;
};

const UNUSABLE = new Set(['locked', 'used', 'expired']);
const FIRST_DEPOSIT_LADDER = /^first_deposit_[1-5]$/;

/**
 * The coupon to apply without asking: the one paying the most on this amount.
 * Ties go to the first-deposit ladder (the one the old sheet auto-picked), then
 * to list order. `null` when nothing usable is quoted.
 */
export function defaultDepositCoupon<T extends DefaultableCoupon>(coupons: T[]): T | null {
  const usable = coupons.filter((coupon) => !UNUSABLE.has(String(coupon.status || '')));
  if (usable.length === 0) return null;
  return usable.reduce((best, coupon) => {
    const bonus = Number(coupon.estimatedBonus || 0);
    const bestBonus = Number(best.estimatedBonus || 0);
    if (bonus > bestBonus + 0.005) return coupon;
    if (Math.abs(bonus - bestBonus) <= 0.005 && FIRST_DEPOSIT_LADDER.test(coupon.code) && !FIRST_DEPOSIT_LADDER.test(best.code)) return coupon;
    return best;
  });
}

export type DefaultDepositBenefit = { kind: 'offer' } | { kind: 'coupon'; id: number } | { kind: 'none' };

/**
 * What the sheet applies without asking, between the cashier's deposit offer
 * and the player's coupons (they are mutually exclusive): the offer whenever
 * it pays at least as much as the best coupon — the PM wants promotions on by
 * default — else the best coupon, else nothing.
 */
export function defaultDepositBenefit<T extends DefaultableCoupon>(input: { offerBonus: number; coupons: T[] }): DefaultDepositBenefit {
  const coupon = defaultDepositCoupon(input.coupons);
  const offerBonus = Number(input.offerBonus || 0);
  const couponBonus = coupon ? Number(coupon.estimatedBonus || 0) : 0;
  if (offerBonus > 0 && offerBonus + 0.005 >= couponBonus) return { kind: 'offer' };
  if (coupon) return { kind: 'coupon', id: coupon.id };
  return { kind: 'none' };
}
